import React from 'react';

import { ApolloError } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import {
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';

import { AttributesFilterFragment } from 'src/generated/mloop-graphql';

type BaseAttributesFilterProps = {
  loading: boolean;
  filterText: string;
  error?: ApolloError;
  data: AttributesFilterFragment[];
  attributeMap?: { [key: string]: string[] };
  onAttributeUpdate: (attributeId: string, attributeValueId, selected: boolean) => void;
};

/**
 * Presentational attribute checkbox list for module seat member filtering.
 */
export const BaseAttributesFilter = ({
  loading,
  error,
  data,
  attributeMap,
  filterText,
  onAttributeUpdate,
}: BaseAttributesFilterProps): JSX.Element | null => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Typography
        variant="body2"
        sx={{
          height: '350px',
          overflowY: 'auto',
        }}
      >
        {t('hiring-dashboard-filter:filter.loading')}
      </Typography>
    );
  }
  if (error) {
    return <Typography variant="body2">{t('hiring-dashboard-filter:filter.error')}</Typography>;
  }

  if (!data) {
    return null;
  }

  const filteredData = filterText
    ? data.map((value) => {
        const tagValues = value?.values?.filter((tagValue) => {
          return (
            value.name.toLowerCase().indexOf(filterText.toLowerCase()) !== -1 ||
            tagValue?.value.toLowerCase().indexOf(filterText.toLowerCase()) !== -1
          );
        });

        if (tagValues?.length === 0) {
          return null;
        }

        const result: AttributesFilterFragment = {
          ...value,
          values: tagValues,
        };

        return result;
      })
    : data;

  return (
    <List
      sx={{
        height: '350px',
        overflowY: 'auto',
        padding: 0,
      }}
    >
      {filteredData.map((value) => {
        if (!value) {
          return null;
        }

        return (
          <>
            <ListSubheader key={value.id} disableSticky disableGutters>
              {value.name}
            </ListSubheader>
            {value?.values?.map((innerValue) => {
              const selectedAttributes = (attributeMap ? attributeMap[value.id] : []) as string[];
              const checked = selectedAttributes ? selectedAttributes.indexOf(innerValue?.id) !== -1 : false;
              const handleChange = () => onAttributeUpdate(value.id, innerValue?.id, !checked);

              return (
                <ListItem key={innerValue?.id}>
                  <ListItemButton onClick={handleChange}>
                    <ListItemIcon>
                      <Checkbox checked={checked} />
                    </ListItemIcon>
                    <ListItemText primary={innerValue?.value} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </>
        );
      })}
    </List>
  );
};
